import DashboardDark from "./DashboardDark";

/**
 * The canvas's `Platform + Dashboard` screen.
 *
 * First of the three dark sections, and the one that establishes the pattern:
 * a recessed `--band` ground and a dot field masked to an ellipse at the top
 * edge so the texture arrives with the section rather than tiling flatly
 * across it.
 *
 * The caption below the rendering is the design's own disclosure that the
 * figures are illustrative. It is content, not chrome.
 */
export default function Platform() {
  return (
    <section
      id="platform"
      className="dc-seam site-gutter"
      style={{
        position: "relative",
        overflow: "clip",
        background: "var(--band)",
        color: "var(--band-ink)",
        paddingTop: 118,
        paddingBottom: 96,
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "radial-gradient(rgba(var(--band-line-rgb),.1) 1px, transparent 1.6px)",
          backgroundSize: "28px 28px",
          opacity: 0.35,
          /* Two masks, intersected. The ellipse is the original: it pools the
             texture at the top so the dots arrive with the section instead of
             tiling flatly down it. On its own, though, it is at FULL opacity at
             y=0, so the texture switched on along the exact line the section
             starts, and that onset was a second seam sitting on top of the
             tonal one. The linear layer takes it to zero across the first
             160px, which keeps the design's intent and removes the edge. */
          WebkitMaskImage:
            "radial-gradient(ellipse 70% 55% at 50% 0%, black, transparent 80%), linear-gradient(to bottom, transparent 0, black 160px)",
          maskImage:
            "radial-gradient(ellipse 70% 55% at 50% 0%, black, transparent 80%), linear-gradient(to bottom, transparent 0, black 160px)",
          WebkitMaskComposite: "source-in",
          maskComposite: "intersect",
          WebkitMaskRepeat: "no-repeat",
          maskRepeat: "no-repeat",
          pointerEvents: "none",
        }}
      />
      <div style={{ maxWidth: 1240, margin: "0 auto", position: "relative" }}>
        {/* Heading beside the lede from tablet up, stacked on a phone
            (global.css, T101026002). */}
        <div data-rv="" className="home-platform-head">
          <div>
            <div
              style={{
                fontFamily: "'Manrope', sans-serif",
                fontSize: 11.5,
                letterSpacing: ".16em",
                textTransform: "uppercase",
                color: "var(--band-acc)",
                marginBottom: 18,
              }}
            >
              <span className="dc-mark" aria-hidden="true" /> All-In-One Platform
            </div>
            <h2
              style={{
                margin: 0,
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 600,
                fontSize: "clamp(28px, 3.4vw, 46px)",
                letterSpacing: "-0.02em",
                lineHeight: 1.14,
              }}
            >
              Run Your Business.
              <br />
              All in One Place.
            </h2>
          </div>
          <div>
            <p
              style={{
                margin: "0 0 18px",
                fontSize: 16,
                color: "rgba(var(--band-ink-rgb),.68)",
              }}
            >
              AI2Aim integrates everything you need to plan, manage, and
              grow, without switching between multiple tools.
            </p>
            <a
              href="#capabilities"
              className="dc-h-cyan-soft"
              style={{ color: "var(--band-acc)", fontWeight: 600, fontSize: 14.5 }}
            >
              See All Features &rarr;
            </a>
          </div>
        </div>

        <div data-rv="" style={{ position: "relative" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: 10,
            }}
          >
            <span
              style={{
                fontFamily: "'Manrope', sans-serif",
                fontSize: 11,
                letterSpacing: ".1em",
                textTransform: "uppercase",
                color: "rgba(var(--band-ink-rgb),.62)",
              }}
            >
              Interface shown with sample data
            </span>
          </div>
          <DashboardDark />
          <p
            style={{
              margin: "20px auto 0",
              maxWidth: 780,
              textAlign: "center",
              fontSize: 12.5,
              color: "rgba(var(--band-ink-rgb),.62)",
            }}
          >
            A rendering of the AI2Aim dashboard: a module sidebar, revenue and
            lead figures, a revenue trend, a task list, and an AI assistant
            answering a question about lead volume. All figures are illustrative
            sample data.
          </p>
        </div>
      </div>
    </section>
  );
}
